/**
 * Building blocks shared by the Grade 9–10 templates (grade09.ts, grade10.ts).
 *
 * Scoped to this grade range (hence the file name) so it never collides with
 * helpers owned by other grade files. Everything here is pure; the only
 * randomness is the Rng passed in, so templates stay deterministic.
 */
import { int, key, type Rational } from '../../math/rational.ts';
import type { Rng } from '../../math/rng.ts';
import type { GeneratedProblemCore, MathMarkup } from '../../types.ts';

export type Instruction = NonNullable<GeneratedProblemCore['instruction']>;

// ───────────────────────────── Difficulty & randomness ─────────────────────────────

/** Difficulty clamped to [0, 1] (NaN counts as 0) so range maths never runs off the ends. */
function clamp01(difficulty: number): number {
  return Number.isNaN(difficulty) ? 0 : Math.min(1, Math.max(0, difficulty));
}

/** Whole-number bound interpolated from `easy` (difficulty 0) to `hard` (difficulty 1). */
export function scaled(difficulty: number, easy: number, hard: number): number {
  return Math.round(easy + (hard - easy) * clamp01(difficulty));
}

/**
 * Weighted pick that ignores options switched off (weight ≤ 0), so templates can
 * gate a variant on difficulty with `weight: d >= 0.5 ? 2 : 0`.
 */
export function choose<T>(rng: Rng, options: readonly (readonly [T, number])[]): T {
  const live = options.filter(([, weight]) => weight > 0).map(([item, weight]) => ({ item, weight }));
  if (!live.length) throw new Error('choose: every option is switched off');
  return rng.weighted(live);
}

/** Letters used for the unknown once a learner is past the gentle end ("x" is not magic). */
const LETTERS = ['x', 'a', 'm', 'p', 'y', 'k'] as const;

/** The unknown's letter: always `x` in the gentle half, any letter above it. */
export function letter(rng: Rng, difficulty: number): string {
  return difficulty < 0.5 ? 'x' : rng.pick(LETTERS);
}

// ───────────────────────────── Integer utilities ─────────────────────────────

export function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) [x, y] = [y, x % y];
  return x;
}

export function lcm(a: number, b: number): number {
  return Math.abs(a * b) / gcd(a, b);
}

/** base^exp for a whole exponent ≥ 0, by repeated multiplication (exact for the small values used here). */
export function ipow(base: number, exp: number): number {
  let out = 1;
  for (let i = 0; i < exp; i++) out *= base;
  return out;
}

/** `a ÷ b` when it is a whole number, else null — for misconceptions that only make sense when they land on an integer. */
export function exactDiv(a: number, b: number): number | null {
  if (b === 0 || a % b !== 0) return null;
  return a / b + 0; // `+ 0` normalizes -0
}

/** Values in [lo, hi] that share a factor with `n` — pass to `rng.intExcept` to draw a number coprime to `n`. */
export function sharingFactorWith(n: number, lo: number, hi: number): number[] {
  const out: number[] = [];
  for (let v = lo; v <= hi; v++) if (gcd(v, n) !== 1) out.push(v);
  return out;
}

// ───────────────────────────── Markup formatting ─────────────────────────────

/**
 * Leading term `c·v`: `3x`, `x`, `-x`, `-3x`. `v` may be any markup (`x^2`).
 * Never called with c = 0 — a template that would print `0x` has a bug.
 */
export function leadTerm(c: number, v: MathMarkup): MathMarkup {
  if (c === 0) throw new Error(`leadTerm: zero coefficient on ${v}`);
  if (c === 1) return v;
  if (c === -1) return `-${v}`;
  return `${c}${v}`;
}

/** Following term with a spaced sign: ` + 3x`, ` - x`; '' when c = 0 (never `+ 0x`, `+ -3x` or `1x`). */
export function nextTerm(c: number, v: MathMarkup): MathMarkup {
  if (c === 0) return '';
  const magnitude = Math.abs(c);
  return `${c < 0 ? ' - ' : ' + '}${magnitude === 1 ? v : `${magnitude}${v}`}`;
}

/** Following constant with a spaced sign: ` + 3`, ` - 3`; '' when 0 (never `x + 0` or `+ -3`). */
export function nextConst(n: number): MathMarkup {
  if (n === 0) return '';
  return n < 0 ? ` - ${-n}` : ` + ${n}`;
}

/** `a·v + b` as markup, e.g. (3, 'x', -2) → `3x - 2`, (1, 'n', 0) → `n`. */
export function linear(a: number, v: MathMarkup, b: number): MathMarkup {
  return `${leadTerm(a, v)}${nextConst(b)}`;
}

/** Bracketed binomial `(a·v + b)`, e.g. (1, 'x', -4) → `(x - 4)`, (2, 'x', 3) → `(2x + 3)`. */
export function binomial(a: number, v: MathMarkup, b: number): MathMarkup {
  return `(${linear(a, v, b)})`;
}

/** A monic quadratic's left side: (b, c) → `x^2 - 5x + 6` (zero terms dropped). */
export function monic(v: MathMarkup, b: number, c: number): MathMarkup {
  return `${v}^2${nextTerm(b, v)}${nextConst(c)}`;
}

/**
 * A blank written after a sign, the conventional way. A negative value is shown
 * as ` - □` and the player types its size, so a completed line never reads
 * `(x + −4)` or `x² + −5x` (content rule: no `+ -3`).
 */
export interface SignedBlank {
  /** ` + ` or ` - ` (spaced), written in front of `\box`. */
  sign: ' + ' | ' - ';
  /** What the player types: the value's size. */
  answer: number;
  /**
   * What a learner types in this box when their working gives the signed value
   * `signed` — maps a misconception's result into the box's form, so the sign
   * slip stays the opposite of the answer. `null` passes through.
   */
  fill(signed: number | null): number | null;
}

/** Sign and answer for a blank whose signed value is `value` (see SignedBlank). */
export function signedBlank(value: number): SignedBlank {
  const negative = value < 0;
  return {
    sign: negative ? ' - ' : ' + ',
    answer: Math.abs(value),
    fill: (signed) => (signed === null ? null : (negative ? -signed : signed) + 0), // `+ 0` normalizes -0
  };
}

// ───────────────────────────── Problem assembly ─────────────────────────────

/** A misconception value; `null` marks "this misconception does not apply / is not a whole number here". */
export type Distractor = number | Rational | null;

/**
 * Assemble a GeneratedProblemCore with an integer answer. Distractors are
 * cleaned here so templates can list every misconception without guarding
 * each one: nulls, non-integers passed as numbers, duplicates and the answer
 * itself are dropped (the generator tops up to three if needed).
 */
export function integerProblem(
  prompt: MathMarkup,
  answer: number,
  distractors: readonly Distractor[],
  instruction?: Instruction,
): GeneratedProblemCore {
  const correct = int(answer);
  const seen = new Set<string>([key(correct)]);
  const cleaned: Rational[] = [];
  for (const d of distractors) {
    if (d === null) continue;
    if (typeof d === 'number' && !Number.isSafeInteger(d)) continue;
    const value = typeof d === 'number' ? int(d) : d;
    const k = key(value);
    if (seen.has(k)) continue;
    seen.add(k);
    cleaned.push(value);
  }
  return {
    prompt,
    ...(instruction ? { instruction } : {}),
    answer: correct,
    answerKind: 'integer',
    distractors: cleaned,
  };
}
